import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

interface EmployeeDto {
  id: number; firstName: string; lastName: string; fullName: string;
  email: string | null; jobTitle: string | null; deleted: boolean;
  roles: string[]; identityLockedOut: boolean;
}
interface Paged<T> { items: T[]; page: number; pageSize: number; totalCount: number; }

export function EmployeesListPage() {
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["employees", page, q, includeDeleted],
    queryFn: () => apiFetch<Paged<EmployeeDto>>(
      `/api/employees?page=${page}&pageSize=50&q=${encodeURIComponent(q)}${includeDeleted ? "" : "&deleted=false"}`),
  });

  const createMut = useMutation({
    mutationFn: (req: { firstName: string; lastName: string; email: string; roles: string[] }) =>
      apiFetch<EmployeeDto>("/api/employees", { method: "POST", body: JSON.stringify(req) }),
    onSuccess: (created) => { setDrawerOpen(false); void qc.invalidateQueries({ queryKey: ["employees"] }); void nav(`/admin/employees/${created.id}`); },
  });

  const cols: DataGridColumn<EmployeeDto>[] = [
    { id: "name",  header: "Name",   accessor: (r) => r.fullName, sortable: true },
    { id: "email", header: "Email",  accessor: (r) => r.email ?? "—", sortable: true },
    { id: "title", header: "Title",  accessor: (r) => r.jobTitle ?? "—", sortable: true },
    { id: "roles", header: "Roles",  accessor: (r) => r.roles.join(", ") || "—", sortable: true },
    { id: "state", header: "State",  accessor: (r) => r.deleted ? "(deleted)" : r.identityLockedOut ? "Locked" : "Active", sortable: true },
  ];

  // Sort state — client-side only; /api/employees has no `sort` param.
  // Sort applies to the current page's rows, not the full result set.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const sortedRows = useMemo(() => {
    const rows = data?.items ?? [];
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: EmployeeDto): string | number | null => {
      switch (sort.columnId) {
        case "name":  return r.fullName;
        case "email": return r.email ?? null;
        case "title": return r.jobTitle ?? null;
        case "roles": return r.roles.join(", ") || null;
        case "state": return r.deleted ? "(deleted)" : r.identityLockedOut ? "Locked" : "Active";
        default:      return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  return (
    <ListPage backTo="/admin"
      title="Employees"
      primaryAction={
        <IntentButton onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Add Employee
        </IntentButton>
      }
      search={{
        value: q,
        onChange: (v) => { setQ(v); setPage(0); },
        placeholder: "Name or email..."
      }}
      toolbarExtra={
        <div className="flex items-center gap-2">
          <Switch id="d" checked={includeDeleted} onCheckedChange={setIncludeDeleted} />
          <Label htmlFor="d" className="text-sm text-slate-600">Show deleted</Label>
        </div>
      }
    >
      <DataGrid
        rows={sortedRows} columns={cols} rowKey={(r) => r.id}
        totalRows={data?.totalCount ?? 0} pageIndex={page} pageSize={50}
        loading={isLoading} onPageChange={setPage} onSortChange={setSort}
      />
      <DetailModal open={drawerOpen} onOpenChange={setDrawerOpen} title="New employee">
        <NewEmployeeForm onSubmit={(req) => createMut.mutate(req)} pending={createMut.isPending} />
      </DetailModal>
    </ListPage>
  );
}

function NewEmployeeForm({ onSubmit, pending }: { onSubmit: (r: { firstName: string; lastName: string; email: string; roles: string[] }) => void; pending: boolean }) {
  const [firstName, setF] = useState(""); const [lastName, setL] = useState(""); const [email, setE] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ firstName, lastName, email, roles: ["ReadOnly"] }); }} className="space-y-3">
      <div><Label htmlFor="fn">First name</Label><Input id="fn" value={firstName} onChange={(e) => setF(e.target.value)} required /></div>
      <div><Label htmlFor="ln">Last name</Label><Input id="ln" value={lastName} onChange={(e) => setL(e.target.value)} required /></div>
      <div><Label htmlFor="em">Email</Label><Input id="em" type="email" value={email} onChange={(e) => setE(e.target.value)} required /></div>
      <Button type="submit" disabled={pending}>{pending ? "Creating…" : "Create & Send Invite"}</Button>
    </form>
  );
}
